import { ReactNode } from 'react'
import { Field, Label, Listbox, ListboxButton } from '@headlessui/react'
import { baseFieldClasses, fieldContainerClasses, labelClasses } from '../at-text-field/shared/classes'
import { FieldHints } from '../at-text-field/shared/field-hints'
import { PortableTextProps } from '@portabletext/react'
import { NavArrowDown, WarningCircle } from '../at-icon'
import { DropdownListbox } from './dropdown-listbox'
import { useRouter } from 'next/compat/router'

export * from './options-generators'

export interface DropdownOption {
  label: string
  value: string
  id?: string
  /** Optional icon rendered to the left of the label in the listbox */
  iconLeft?: ReactNode
}

export interface DropdownOptionParams {
  options: DropdownOption[]
  value?: DropdownOption
  onChange?: (value: DropdownOption) => void
  hasBuildings?: boolean
}

export interface AtDropdownProps extends DropdownOptionParams {
  label?: string
  placeholder?: string
  helperText?: PortableTextProps['value']
  errorText?: PortableTextProps['value']
  hasError?: boolean
  disabled?: boolean
  name: string
  required?: boolean
}

export const AtDropdown = ({
  errorText,
  hasError,
  helperText,
  label,
  name,
  onChange,
  options,
  placeholder,
  value,
  disabled = false,
  required,
}: AtDropdownProps) => {
  const router = useRouter()

  return (
    <Field className={`${fieldContainerClasses}`} disabled={disabled}>
      <Label className={labelClasses}>
        {label}
        {required && <span className="text-feedback-negative-300 ml-1">*</span>}
      </Label>
      <Listbox value={value} onChange={onChange} by={(a, b) => a?.value === b?.value} name={name}>
        <ListboxButton
          className={`${baseFieldClasses(
            hasError,
            disabled
          )} h-14 p-4 flex justify-between ui-open:border-neutrals-800`}
        >
          <span
            className={`grow-1 leading-1.5 ${value?.label ? '' : 'text-neutrals-800 ui-open:text-neutrals-900'} ${
              disabled ? 'text-neutrals-400 ' : ''
            }`}
          >
            {value?.label ? value.label : placeholder}
          </span>
          <div
            className={`flex gap-1 ${disabled ? 'text-neutrals-400 ' : hasError ? 'text-feedback-negative-300' : ''}`}
          >
            {hasError && !disabled && <WarningCircle />}
            <NavArrowDown className="ui-open:rotate-180" />
          </div>
        </ListboxButton>
        <DropdownListbox
          hasBuildings={router?.query.building ? true : false}
          options={options}
          className="absolute top-[5.25rem] w-full z-[2] !w-[var(--button-width)]"
        />
      </Listbox>
      <FieldHints errorText={errorText} hasError={hasError} helperText={helperText} />
    </Field>
  )
}
